import React from 'react'
import { clsx } from 'clsx'

/**
 * Input 组件的 props 接口
 */
interface InputProps {
  /** 输入框类型 */
  type?: 'text' | 'email' | 'password' | 'number' | 'search' | 'url' | 'tel'
  /** 输入框大小 */
  size?: 'sm' | 'md' | 'lg'
  /** 是否禁用 */
  disabled?: boolean
  /** 是否为只读 */
  readOnly?: boolean
  /** 是否有错误 */
  error?: boolean
  /** 占位符文本 */
  placeholder?: string
  /** 输入框值 */
  value?: string | number
  /** 默认值 */
  defaultValue?: string | number
  /** 输入框左侧图标 */
  leftIcon?: React.ReactNode
  /** 输入框右侧图标 */
  rightIcon?: React.ReactNode
  /** 自定义类名 */
  className?: string
  /** 输入框容器类名 */
  containerClassName?: string
  /** 变更事件处理器 */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  /** 焦点事件处理器 */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  /** 失焦事件处理器 */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  /** 键盘事件处理器 */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  /** 其他 HTML input 属性 */
  [key: string]: any
}

/**
 * 基础输入框组件
 * 支持图标、错误状态、多种大小和类型
 */
export const Input: React.FC<InputProps> = ({
  type = 'text',
  size = 'md',
  disabled = false,
  readOnly = false,
  error = false,
  placeholder,
  value,
  defaultValue,
  leftIcon,
  rightIcon,
  className,
  containerClassName,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  ...props
}) => {
  /**
   * 获取输入框样式（使用CSS变量）
   */
  const getInputStyles = () => {
    const baseStyles = clsx(
      // 基础样式
      'flex-1 block w-full transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-0',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      
      // 大小样式
      {
        'px-3 py-1.5 text-sm rounded-md': size === 'sm',
        'px-3 py-2 text-sm rounded-lg': size === 'md',
        'px-4 py-3 text-base rounded-xl': size === 'lg',
      },
      
      // 图标存在时的内边距调整
      {
        'pl-10': leftIcon && size === 'sm',
        'pl-11': leftIcon && size === 'md',
        'pl-13': leftIcon && size === 'lg',
      },
      {
        'pr-10': rightIcon && size === 'sm',
        'pr-11': rightIcon && size === 'md',
        'pr-13': rightIcon && size === 'lg',
      },
      
      className
    )

    // 根据状态设置样式
    let inputStyles = {}
    if (error) {
      inputStyles = {
        border: '1px solid #dc3545',
        background: 'var(--bg-primary)',
        color: 'var(--text-primary)'
      }
    } else {
      inputStyles = {
        border: '1px solid var(--border-medium)',
        background: 'var(--bg-primary)',
        color: 'var(--text-primary)'
      }
    }

    return { className: baseStyles, style: inputStyles }
  }

  /**
   * 获取容器样式
   */
  const getContainerStyles = () => {
    return clsx(
      'relative flex items-center',
      containerClassName
    )
  }

  /**
   * 获取图标样式
   */
  const getIconStyles = (position: 'left' | 'right') => {
    const baseClasses = 'absolute pointer-events-none text-gray-400'
    
    return clsx(
      baseClasses,
      {
        // 位置
        'left-3': position === 'left',
        'right-3': position === 'right',
        
        // 大小
        'w-4 h-4': size === 'sm',
        'w-5 h-5': size === 'md',
        'w-6 h-6': size === 'lg',
      }
    )
  }

  /**
   * 渲染左侧图标
   */
  const renderLeftIcon = () => {
    if (!leftIcon) return null
    
    const iconElement = leftIcon as React.ReactElement<any>
    return (
      <div className={getIconStyles('left')}>
        {React.cloneElement(iconElement, {
          className: clsx(
            iconElement.props.className,
            'w-full h-full'
          )
        })}
      </div>
    )
  }

  /**
   * 渲染右侧图标
   */
  const renderRightIcon = () => {
    if (!rightIcon) return null
    
    const iconElement = rightIcon as React.ReactElement<any>
    return (
      <div className={getIconStyles('right')}>
        {React.cloneElement(iconElement, {
          className: clsx(
            iconElement.props.className,
            'w-full h-full'
          )
        })}
      </div>
    )
  }

  const inputStyles = getInputStyles()

  return (
    <div className={getContainerStyles()}>
      {renderLeftIcon()}
      <input
        type={type}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className={inputStyles.className}
        style={inputStyles.style}
        {...props}
      />
      {renderRightIcon()}
    </div>
  )
}

/**
 * Input 组件的显示名称
 */
Input.displayName = 'Input'

/**
 * 导出 InputProps 类型
 */
export type { InputProps }